import type { Blockquote, Paragraph, PhrasingContent, Root } from "mdast";
import { visit } from "unist-util-visit";

/** The label needs its colon: "Read more details before signing" is prose, not a fold. */
const LABEL = /^\s*read more:\s*/i;
const BOLD_LABEL = /^\s*read more\s*$/i;

/**
 * A blockquote opening with "Read more:" (bold or plain) is the technical
 * part of a reply, to be folded away. This remark plugin strips the label
 * from the quote's text and marks the node so MessageBubble can draw it as
 * a ReadMore; the label becomes the summary, and is not shown twice.
 * See docs/superpowers/specs/2026-10-14-plain-words-read-more-design.md.
 */
export default function remarkReadMore() {
  return (tree: Root) => {
    visit(tree, "blockquote", (node: Blockquote) => {
      const paragraph = node.children[0];
      if (!paragraph || paragraph.type !== "paragraph" || !stripLabel(paragraph)) return;
      node.data = { ...node.data, hProperties: { ...(node.data?.hProperties as object | undefined), "data-read-more": "true" } };
    });
  };
}

/** Removes the leading label from the paragraph; false when it does not open with one. */
function stripLabel(paragraph: Paragraph): boolean {
  const first = paragraph.children[0];
  if (!first) return false;
  if (first.type === "strong") {
    const text = first.children.map(plain).join("");
    const next = paragraph.children[1];
    // "**Read more:** text" or "**Read more**: text"; the colon may sit on either side of the bold.
    const colonInside = LABEL.test(text) && !text.replace(LABEL, "").trim();
    const colonOutside = BOLD_LABEL.test(text) && next?.type === "text" && /^\s*:/.test(next.value);
    if (!colonInside && !colonOutside) return false;
    paragraph.children.shift();
    if (colonOutside && next.type === "text") next.value = next.value.replace(/^\s*:/, "");
    trimLeading(paragraph.children);
    return true;
  }
  if (first.type === "text" && LABEL.test(first.value)) {
    first.value = first.value.replace(LABEL, "");
    if (!first.value) paragraph.children.shift();
    return true;
  }
  return false;
}

function trimLeading(children: PhrasingContent[]): void {
  const next = children[0];
  if (next?.type === "text") {
    next.value = next.value.replace(/^\s+/, "");
    if (!next.value) children.shift();
  }
}

function plain(node: PhrasingContent): string {
  if (node.type === "text" || node.type === "inlineCode") return node.value;
  return "children" in node ? node.children.map(plain).join("") : "";
}
